import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import PackCard from "./PackCard";
import EventHistoryDrawer from "./EventHistoryDrawer";
import CartLoader from "../../../../../Helper/Loaders/CartLoader";
import { useVehicleInfo, useBatteryInfo } from "../../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { usePermissions } from "../../../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../../../Static/FeatureMapping";
import { getBatteryComponents } from "../../../../../Api/ServiceRecordApi";
import { getEntityTags } from "../../../../../Api/TagsApi";
import { transformTwinHierarchyData } from "../../../../../Helper/DigitalTwin/DigitalTwinFunctions";
import {
    extractStringsWithPacks,
    formatBmsSoh,
    getReplaceBlockedReason,
    isReplaceVisible,
    isPackSelectableForMode,
    getSelectionModeCopy,
    SELECTION_MODE_EDIT,
    collectSelectedPacks,
    formatSelectionCount,
    buildPackSelectionParams,
} from "../../../../../Helper/DigitalTwin/BatteryComponentsHelper";
import { encryptTheParams } from "../../../../../Helper/QueryParams/EncryptDecrypt";

// Resolve the bus operational status from its entity tags.
// Returns true only when explicitly non_operational.
const resolveNonOperational = (tagsRes) => {
    const tags = tagsRes?.response?.data?.tags || [];
    const statusTag = tags.find((tag) => tag.tagCode === "status");
    return statusTag?.valueCode === "non_operational";
};

export default function StringsPacksGrid({
    batteryID,
    // "Replace Multiple Packs" is owned by the Components tab (the button sits above
    // this grid, outside it), so the mode is passed in and exiting is reported back up.
    // null when idle, else SELECTION_MODE_REPLACE / SELECTION_MODE_EDIT.
    selectionMode = null,
    onExitSelectionMode = () => {},
    // The bus/permission gate is resolved here (it needs the entity tags), but the
    // header button that opens selection mode has to know about it.
    onReplaceGateChange = () => {},
}) {
    const { t } = useTranslation(["digital_twin", "common"]);
    const navigate = useNavigate();
    const vehicleInfo = useVehicleInfo();
    const { pageType, operationalStatusVersion } = useBatteryInfo();
    const { hasPermission, isRbacV2Enabled } = usePermissions();

    // Gated on the BATTERY_COMPONENTS *create* permission, which is what the
    // create endpoints themselves require (@feature_access_required(...,
    // action="create") on POST /api/service-records and the document upload).
    // This used to ask for "update", so a user holding one and not the other was
    // either shown a button that 403s on submit, or refused one they could use.
    // v1 orgs fall back to true.
    const canEdit = !isRbacV2Enabled()
        ? true
        : hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, "create");

    // Separate from canEdit on purpose. Editing a pack's recorded details is gated on
    // BATTERY_COMPONENTS *update*, which is what PUT /api/batteries/<id>/packs itself
    // requires -- reusing canEdit here would show the pencil to a user holding
    // create-but-not-update and 403 them on submit, which is the exact mismatch the
    // comment above records happening once already. v1 orgs fall back to true.
    const canEditDetails = !isRbacV2Enabled()
        ? true
        : hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, "update");

    const [isLoading, setIsLoading] = useState(true);
    const [strings, setStrings] = useState([]);
    const [isNonOperational, setIsNonOperational] = useState(false);
    const [lastNonOperationalAt, setLastNonOperationalAt] = useState(null);
    const [error, setError] = useState(false);
    const [historyDrawer, setHistoryDrawer] = useState({
        open: false,
        absPackNo: null,
        chipLabel: "",
    });

    // Selected slot numbers (absPackNo), not packConfigIDs: absPackNo is what the
    // Record Replacement form is handed and it survives a replacement.
    const [selectedAbsPackNos, setSelectedAbsPackNos] = useState([]);

    // Guards setState against a fetch resolving after unmount (strict-mode safe:
    // reset to true on each mount, false on cleanup).
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Components tree (packs/strings). Extracted so it can be re-run both on mount
    // and on tab re-focus. showSpinner=false does a silent background refresh
    // (no full-page loader flicker).
    const fetchComponents = useCallback(
        async ({ showSpinner = true } = {}) => {
            if (showSpinner) setIsLoading(true);
            setError(false);
            try {
                const componentsRes = await getBatteryComponents(batteryID);
                if (!mountedRef.current) return;
                const batterySystem = componentsRes?.response?.BATTERY_SYSTEM || [];
                const tree = transformTwinHierarchyData(batterySystem, t);
                setStrings(extractStringsWithPacks(tree));
                setLastNonOperationalAt(componentsRes?.response?.lastNonOperationalAt || null);
            } catch (err) {
                console.error("Error fetching battery components:", err);
                if (mountedRef.current) setError(true);
            } finally {
                if (mountedRef.current && showSpinner) setIsLoading(false);
            }
        },
        [batteryID, t]
    );

    // Initial load + battery change.
    useEffect(() => {
        fetchComponents({ showSpinner: true });
    }, [fetchComponents]);

    // Record Replacement opens in a separate tab; when the user returns to this
    // tab after recording a replacement, silently refresh so a retired pack's
    // stale serial / packConfigID isn't shown (which would 404 on a re-replace).
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                fetchComponents({ showSpinner: false });
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [fetchComponents]);

    // Operational status — re-read whenever it's changed via the header modal
    // (operationalStatusVersion bump), so Replace enables/disables live.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const tagsRes = await getEntityTags("battery", batteryID);
                if (active) setIsNonOperational(resolveNonOperational(tagsRes));
            } catch (err) {
                console.error("Error fetching operational status:", err);
            }
        })();
        return () => {
            active = false;
        };
    }, [batteryID, operationalStatusVersion]);

    // Whether replacing is permitted, and why not. Reported up so the "Replace
    // Multiple Packs" button behaves exactly as the per-card Replace button does:
    // hidden without permission, shown and explaining itself when the only thing
    // in the way is the bus still being operational.
    const isVisible = isReplaceVisible({ canEdit });
    const blockedReason = getReplaceBlockedReason({ isNonOperational });
    useEffect(() => {
        onReplaceGateChange({
            isVisible,
            canReplace: isVisible && blockedReason === null,
            blockedReason,
            // Reported alongside so "Edit Multiple Packs" appears on the same rule the
            // per-card pencil uses. Deliberately independent of blockedReason: editing
            // is not gated on the bus being Non-operational.
            // Gated on the grid actually having packs to pick. The action bar that
            // exits selection mode renders only on the happy path, so entering the
            // mode over a loading, errored or empty grid left no control anywhere on
            // screen to leave it.
            canEditDetails: canEditDetails && !isLoading && !error && strings.length > 0,
        });
    }, [
        isVisible,
        blockedReason,
        canEditDetails,
        isLoading,
        error,
        strings.length,
        onReplaceGateChange,
    ]);

    // Leaving selection mode discards the selection — re-entering should never
    // resurrect ticks the user cancelled out of.
    useEffect(() => {
        if (!selectionMode) setSelectedAbsPackNos([]);
    }, [selectionMode]);

    const togglePack = (pack) =>
        setSelectedAbsPackNos((prev) =>
            prev.some((no) => String(no) === String(pack?.absPackNo))
                ? prev.filter((no) => String(no) !== String(pack?.absPackNo))
                : [...prev, pack?.absPackNo]
        );

    // Both entry points go through here: the per-card Replace button passes one
    // pack, "Continue to Replacement" passes the selection in slot order.
    const openRecordReplacement = (packs) => {
        if (!packs.length) return;
        const params = buildPackSelectionParams({
            batteryID,
            packs,
            lastNonOperationalAt,
            vehicleNumber: vehicleInfo?.licensePlateNo || "",
            pageType,
        });
        encryptTheParams(params, navigate, false, "/record-replacement", true);
    };

    const handleReplace = (pack, string) =>
        openRecordReplacement([{ ...pack, stringLabel: string?.label || "" }]);

    const selectedPacks = useMemo(
        () => collectSelectedPacks(strings, selectedAbsPackNos),
        [strings, selectedAbsPackNos]
    );

    // Both edit entry points go through here: the per-card pencil passes one pack,
    // "Continue to Edit" passes the selection in slot order. Same hand-off shape as
    // openRecordReplacement -- only the slot numbers travel, and the page re-resolves
    // everything else from the components tree so it never works from a stale serial.
    const openEditPackDetails = (selection) => {
        if (!selection.length) return;
        const editParams = buildPackSelectionParams({
            batteryID,
            packs: selection,
            lastNonOperationalAt,
            vehicleNumber: vehicleInfo?.licensePlateNo || "",
            pageType,
        });
        encryptTheParams(editParams, navigate, false, "/edit-pack-details", true);
    };

    // Record Replacement opens in a NEW browser tab, so this one stays mounted. Leaving it
    // in selection mode would strand a sticky action bar over a selection that has already
    // been handed off - and re-clicking Continue would open the flow a second time. Drop
    // back to the plain grid the moment the hand-off happens; the visibilitychange refresh
    // above then picks up the result when the user returns.
    const handleContinue = () => {
        if (!selectedPacks.length) return;
        if (selectionMode === SELECTION_MODE_EDIT) {
            openEditPackDetails(selectedPacks);
            onExitSelectionMode();
            return;
        }
        openRecordReplacement(selectedPacks);
        onExitSelectionMode();
    };

    const handleHistory = (pack, string) => {
        setHistoryDrawer({
            open: true,
            // Group history by the stable slot (absPackNo), not the current pack's
            // packConfigID (which changes each replacement under append-only).
            absPackNo: pack?.absPackNo ?? null,
            chipLabel: [string?.label, pack?.label].filter(Boolean).join(" "),
        });
    };

    const closeHistory = () => setHistoryDrawer((prev) => ({ ...prev, open: false }));

    const handleEditDetails = (pack, string) =>
        openEditPackDetails([{ ...pack, stringLabel: string?.label || "" }]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "240px",
                }}
            >
                <CartLoader />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px", color: "#8D8D8D" }}>
                    Unable to load battery components. Please try again.
                </Typography>
            </Box>
        );
    }

    if (!strings.length) {
        return (
            <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px", color: "#8D8D8D" }}>
                    No packs found for this bus.
                </Typography>
            </Box>
        );
    }

    return (
        <>
        {/* One string per row (full width); packs keep a fixed width and lay out
            left-to-right within the string. */}
        <Box sx={{ mt: "20px", mb: 8, display: "flex", flexDirection: "column", gap: "24px" }}>
            {strings.map((string) => (
                <Box
                    key={string.id}
                    sx={{
                        border: "1px solid #E6E6E6",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        padding: "20px 24px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: "20px",
                        }}
                    >
                        <Typography
                            sx={{ fontSize: "16px", fontWeight: 700, color: "#1A1A1A" }}
                        >
                            {string.label}
                        </Typography>
                        <Typography
                            sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}
                        >
                            BMS SoH {formatBmsSoh(string.sohBms)}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(4, minmax(0, 1fr))",
                            },
                            gap: "16px",
                        }}
                    >
                        {string.packs.map((pack) => (
                            <PackCard
                                key={pack.id}
                                pack={pack}
                                isNonOperational={isNonOperational}
                                canEdit={canEdit}
                                canEditDetails={canEditDetails}
                                onReplace={(p) => handleReplace(p, string)}
                                onEditDetails={(p) => handleEditDetails(p, string)}
                                onHistory={(p) => handleHistory(p, string)}
                                selectionMode={!!selectionMode}
                                selected={selectedAbsPackNos.some(
                                    (no) => String(no) === String(pack.absPackNo)
                                )}
                                selectable={isPackSelectableForMode(pack, selectionMode, {
                                    canEdit,
                                    canEditDetails,
                                    isNonOperational,
                                })}
                                onToggleSelect={togglePack}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>

        {/* Selection action bar — sticks to the bottom of the viewport while the
            user scrolls through the strings picking packs. */}
        {selectionMode && (
            <Box
                sx={{
                    position: "sticky",
                    bottom: "16px",
                    zIndex: 5,
                    mt: "-48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                    backgroundColor: "#fff",
                    border: "1px solid #E6E6E6",
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(2, 6, 24, 0.10)",
                    padding: "14px 20px",
                }}
            >
                <Typography
                    data-testid="selection-count"
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}
                >
                    {formatSelectionCount(selectedPacks.length)}
                </Typography>

                <Box sx={{ display: "flex", gap: "12px" }}>
                    <Button
                        data-testid="selection-cancel"
                        onClick={onExitSelectionMode}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#494949",
                            border: "1px solid #D0D0D0",
                            borderRadius: "8px",
                            padding: "8px 26px",
                            "&:hover": { backgroundColor: "#F5F5F5" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid={getSelectionModeCopy(selectionMode).testId}
                        disableElevation
                        disabled={!selectedPacks.length}
                        onClick={handleContinue}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#fff",
                            backgroundColor: "#6D72F6",
                            borderRadius: "8px",
                            padding: "8px 26px",
                            "&:hover": { backgroundColor: "#5A5FE0" },
                            "&.Mui-disabled": { backgroundColor: "#BEC0FA", color: "#fff" },
                        }}
                    >
                        {getSelectionModeCopy(selectionMode).continueLabel}
                    </Button>
                </Box>
            </Box>
        )}

        <EventHistoryDrawer
            open={historyDrawer.open}
            onClose={closeHistory}
            batteryID={batteryID}
            absPackNo={historyDrawer.absPackNo}
            chipLabel={historyDrawer.chipLabel}
            vehicleNumber={vehicleInfo?.licensePlateNo || ""}
        />

        </>
    );
}
